import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OnboardingOptionCardProps {
  heading?: string;
  title: string;
  description?: string;
  eyebrow?: string;
  illustration?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  pending?: boolean;
  pendingLabel?: string;
  compact?: boolean;
  onClick: () => void;
}

export const OnboardingOptionCard = ({
  heading,
  title,
  description,
  eyebrow,
  illustration,
  selected = false,
  disabled = false,
  pending = false,
  pendingLabel,
  compact = false,
  onClick,
}: OnboardingOptionCardProps) => (
  <button
    type="button"
    aria-pressed={selected}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'group relative flex w-full overflow-hidden rounded-[22px] border bg-card text-left text-foreground outline-none transition-[border-color,background-color,transform,box-shadow] duration-200',
      'hover:-translate-y-0.5 hover:border-primary/60 hover:bg-secondary hover:shadow-[0_14px_36px_hsl(var(--primary)/0.07)]',
      'focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-background',
      'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0',
      'motion-reduce:transform-none motion-reduce:transition-none',
      selected
        ? 'border-primary/80 bg-secondary shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]'
        : 'border-border',
      compact
        ? 'min-h-28 flex-col justify-center p-4 sm:min-h-36 sm:p-5'
        : 'min-h-[148px] flex-row items-center p-4 sm:min-h-72 sm:flex-col sm:items-stretch sm:p-6',
    )}
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/45 to-transparent"
    />

    {illustration && (
      <span className="mr-4 flex h-20 w-20 shrink-0 items-center justify-center text-foreground sm:mb-5 sm:mr-0 sm:h-32 sm:w-auto">
        {illustration}
      </span>
    )}

    <span className={cn('flex min-w-0 flex-col', !compact && 'flex-1')}>
      {eyebrow && (
        <span className="mb-3 font-mono text-xs font-semibold tracking-[0.18em] text-primary">
          {eyebrow}
        </span>
      )}

      {heading && (
        <span className="text-lg font-semibold leading-tight text-foreground sm:text-xl">
          {heading}
        </span>
      )}

      <span
        className={cn(
          'leading-snug',
          heading
            ? 'mt-1 text-sm font-medium text-muted-foreground sm:text-[15px]'
            : 'text-base font-semibold text-foreground sm:text-lg',
        )}
      >
        {pending ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            {pendingLabel}
          </span>
        ) : (
          title
        )}
      </span>
      {description && (
        <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {description}
        </span>
      )}
    </span>
  </button>
);
